/*
 * Copyright (c) 2024-2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import { TableInitBase } from 'utils/hooks/useTable'
import { ColumnFiltersState } from '@tanstack/react-table'

export const getColumnFilter = (
    tableInit: TableInitBase,
    id: string
): string | undefined => {
    const entry = (tableInit?.columnFilters || []).find((f: ColumnFiltersState[number]) => f.id === id)
    return entry?.value as string | undefined || undefined
}

export const normalizeSearch = (value: unknown): string | undefined => {
    if (value == null) return undefined
    if (typeof value === 'string') {
        const trimmedValue = value.trim()
        return trimmedValue.length ? trimmedValue : undefined
    }
    return undefined
}

export const buildTableQueryParams = (opts: {
    tableInit: TableInitBase
    searchFrom?: string[]
    extras?: Record<string, unknown>
}) => {
    const { tableInit, searchFrom = [], extras = {} } = opts
    const parts: (string | undefined)[] = []
    for (const id of searchFrom) {
        parts.push(normalizeSearch(getColumnFilter(tableInit, id)))
    }
    parts.push(normalizeSearch(tableInit?.globalFilter))
    const search = parts.find((part) => typeof part === 'string')
    const base: any = {
        page: (tableInit?.pageIndex ?? 0) + 1,
        per_page: tableInit?.pageSize ?? 50,
        sorting: tableInit?.sorting,
    }
    if (search) base.search = search
    return { ...base, ...extras }
}

export const buildTableRequest = (opts: {
    tableInit: TableInitBase
    extras?: Record<string, unknown>
    includeGlobalAsAll?: boolean
    includeColumnVisibility?: boolean
    zeroBasedPage?: boolean
    splitSorting?: boolean
    encodeSearchAsJsonString?: boolean
    transformSearch?: (filters: any[]) => any[]
    transformHiddenColumns?: (args: {
        hiddenColumnIds: string[]
        columnVisibility?: Record<string, boolean>
    }) => string[]
    transformSorting?: (sorting: { id: string; desc: boolean }[]) => { id: string; desc: boolean }[]
}) => {
    const {
        tableInit,
        extras = {},
        includeGlobalAsAll = true,
        includeColumnVisibility = false,
        zeroBasedPage = false,
        splitSorting = false,
        encodeSearchAsJsonString = false,
        transformSearch,
        transformHiddenColumns,
        transformSorting,
    } = opts

    const normalizeHiddenColumns = (columnIds: string[]) => {
        const normalized = new Set<string>()
        const hasHiddenCompanyColumns = columnIds.some(
            (columnId) => columnId === 'company' || columnId.startsWith('company_')
        )

        columnIds.forEach((columnId) => {
            if (columnId === 'company' || columnId.startsWith('company_')) {
                return
            }
            normalized.add(columnId)
        })

        if (hasHiddenCompanyColumns) {
            normalized.add('company.*')
        }

        return Array.from(normalized)
    }

    const filters = Array.isArray(tableInit?.columnFilters)
        ? tableInit.columnFilters.filter(
              (filterItem: any) => filterItem?.id !== 'all'
          )
        : []
    const search =
        includeGlobalAsAll && normalizeSearch(tableInit?.globalFilter)
            ? [...filters, { id: 'all', value: tableInit.globalFilter }]
            : filters
    const rawSorting = Array.isArray(tableInit?.sorting) ? tableInit.sorting : []
    const sortingArr =
        typeof transformSorting === 'function' ? transformSorting(rawSorting) : rawSorting
    const firstSort = sortingArr.length > 0 ? sortingArr[0] : null
    const columnVisibility =
        tableInit?.columnVisibility && typeof tableInit.columnVisibility === 'object'
            ? tableInit.columnVisibility
            : undefined
    const hiddenColumns = columnVisibility
        ? normalizeHiddenColumns(
              Object.entries(columnVisibility)
              .filter(([, isVisible]) => isVisible === false)
              .map(([columnId]) => columnId)
          )
        : []
    const transformedHiddenColumns =
        typeof transformHiddenColumns === 'function'
            ? transformHiddenColumns({
                  hiddenColumnIds: hiddenColumns,
                  columnVisibility,
              })
            : hiddenColumns
    const base: any = {
        page: zeroBasedPage
            ? (tableInit?.pageIndex ?? 0)
            : (tableInit?.pageIndex ?? 0) + 1,
        per_page: tableInit?.pageSize ?? 50,
        search: undefined,
        sorting: sortingArr,
    }
    if (splitSorting) {
        base.sort_by = firstSort?.id
        base.sort = firstSort
            ? firstSort.desc
                ? 'desc'
                : 'asc'
            : undefined
    }
    if (includeColumnVisibility) {
        base.hidden_columns = transformedHiddenColumns
    }
    const transformed =
        typeof transformSearch === 'function'
            ? transformSearch(
                  typeof tableInit?.clearNullableFilters === 'function'
                      ? tableInit.clearNullableFilters(search)
                      : search
              )
            : typeof tableInit?.clearNullableFilters === 'function'
              ? tableInit.clearNullableFilters(search)
              : search
    const clearedSearch =
        typeof tableInit?.clearNullableFilters === 'function'
            ? tableInit.clearNullableFilters(transformed)
            : transformed
    if (encodeSearchAsJsonString) {
        base.search =
            Array.isArray(clearedSearch) && clearedSearch.length > 0
                ? JSON.stringify(clearedSearch)
                : null
    } else {
        base.search = clearedSearch
    }
    return { ...base, ...extras }
}
